/**
 * Textes de la facturation (lot L01, 2026-10-10 ; français, langue de référence) : factures,
 * transactions, moyens de paiement. Branchés dans `fr.ts` sous `billing` ; `billing.en.ts` porte
 * exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const billingFr = {
  common: {
    created: 'Créé',
    updated: 'Mis à jour',
    unknown: 'Inconnu',
    close: 'Fermer',
    allStatuses: 'Tous les statuts',
    notConfigured: 'Non renseigné par l’opérateur',
    forbidden:
      'Refusé : la facturation est réservée aux administrateurs de l’organisation. Demandez-leur ce droit.',
  },
  providers: {
    manual: 'Manuel (virement, prélèvement SEPA)',
    stripe: 'Stripe',
    platform: 'Plateforme',
    other: 'Autre',
  },
  invoices: {
    title: 'Factures',
    subtitle:
      'Les factures mensuelles de l’organisation. Une facture impayée se règle par prélèvement SEPA ou par virement, avec son numéro en référence.',
    searchPlaceholder: 'Filtrer par numéro…',
    columns: {
      number: 'Numéro',
      period: 'Période',
      status: 'Statut',
      subTotal: 'Hors taxes',
      taxes: 'Taxes',
      total: 'Total TTC',
    },
    statuses: {
      UNPAID: 'Impayée',
      PAID: 'Payée',
      CANCELLED: 'Annulée',
      ISSUED: 'Avoir émis',
      REFUND_DUE: 'À rembourser',
    },
    empty: {
      title: 'Aucune facture',
      description: 'La première facture est émise à la fin du premier mois d’utilisation.',
    },
    download: 'Télécharger le PDF',
    downloadLabel: 'Télécharger la facture {{id}} en PDF',
    downloadFailed: 'Téléchargement impossible : {{detail}}',
    downloadUnavailable: 'Le rendu PDF des factures n’est pas activé sur cette installation.',
    detail: {
      title: 'Facture {{id}}',
      tabs: { summary: 'Résumé', transactions: 'Paiements' },
      from: 'Début de période',
      to: 'Fin de période',
      transferReference: 'Référence de virement',
      transactionsHint: 'Les paiements engagés pour cette facture.',
      noTransaction: 'Aucun paiement pour cette facture',
    },
    pay: {
      action: 'Payer',
      title: 'Payer la facture {{id}}',
      description: 'Choisissez le moyen de paiement. Un virement reste en attente jusqu’à sa réception.',
      field: 'Moyen de paiement',
      submit: 'Payer',
      done: 'Paiement de la facture {{id}} engagé.',
      none: 'Aucun moyen de paiement valide : ajoutez un mandat SEPA ou le virement bancaire pour payer.',
      openTransaction: 'Voir la transaction',
    },
    creditNote: {
      badge: 'Avoir',
      action: 'Annuler par un avoir',
      title: 'Annuler la facture {{id}} ?',
      description:
        'Une facture émise ne se supprime pas : un avoir numéroté dans la même séquence l’annule, avec son PDF signé. Les paiements encore ouverts de la facture sont annulés. Ce geste ne se défait pas.',
      descriptionPaid:
        'La facture {{id}} est payée : l’avoir qui l’annule sera « à rembourser », le remboursement se fait hors de graal. Ce geste ne se défait pas.',
      submit: 'Émettre l’avoir',
      done: 'Avoir {{id}} émis : la facture {{invoice}} est annulée.',
      failed: 'Annulation impossible : {{detail}}',
      detailTitle: 'Avoir {{id}}',
      credits: 'Annule la facture',
      cancelledBy: 'Annulée par l’avoir',
    },
    generate: {
      action: 'Générer la facture du mois',
      title: 'Générer la facture d’un mois',
      description:
        'Administrateur de la plateforme : calcule la facture de l’organisation {{tenant}} pour le mois choisi, à partir de sa consommation.',
      field: 'Mois',
      fieldHint: 'Au format AAAA-MM. Par défaut, le mois précédent.',
      submit: 'Générer',
      done: 'Facture {{id}} générée.',
      errors: { month: 'Un mois au format AAAA-MM (par exemple 2026-09).' },
    },
  },
  transactions: {
    title: 'Transactions',
    subtitle:
      'Chaque paiement d’une facture. Un virement ou un prélèvement reste « en attente » jusqu’à ce que l’opérateur constate l’encaissement.',
    searchPlaceholder: 'Filtrer par transaction, facture ou référence…',
    columns: {
      id: 'Transaction',
      invoice: 'Facture',
      status: 'Statut',
      provider: 'Fournisseur',
      reference: 'Référence',
      total: 'Montant',
    },
    statuses: {
      scheduled: 'Planifiée',
      pending: 'En attente',
      commited: 'Engagée',
      succeeded: 'Encaissée',
      failed: 'Échouée',
      cancelled: 'Annulée',
    },
    empty: {
      title: 'Aucune transaction',
      description: 'Payer une facture crée une transaction.',
    },
    reference: 'Référence de paiement',
    referenceHint: 'À indiquer en libellé du virement : c’est elle qui rattache le paiement à la facture.',
    detail: {
      title: 'Transaction',
      tabs: { summary: 'Résumé' },
      paymentMethod: 'Moyen de paiement',
      initiator: 'Initiée par',
    },
    sepa: {
      action: 'Fichier de prélèvement SEPA',
      title: 'Fichier de prélèvement SEPA',
      description:
        'Administrateur de la plateforme : les prélèvements dus de toutes les organisations, en un fichier pain.008 à déposer à votre banque. Chaque paiement passe « engagé » ; marquez-le reçu une fois le relevé lu.',
      date: 'Date d’échéance demandée',
      dateHint: 'Par défaut, dans deux jours.',
      submit: 'Générer le fichier',
      done: 'Fichier de prélèvement téléchargé.',
      nothing: 'Aucun prélèvement à présenter.',
      failed: 'Fichier impossible : {{detail}}',
    },
    settle: {
      succeeded: 'Marquer reçu',
      failedAction: 'Marquer échoué',
      confirm: {
        succeeded: {
          title: 'Marquer le paiement comme reçu ?',
          description:
            'La facture {{invoice}} passe « payée » et part par courriel avec son PDF. Ce geste ne se défait pas.',
        },
        failed: {
          title: 'Marquer le paiement comme échoué ?',
          description: 'La facture {{invoice}} reste impayée ; un nouveau paiement pourra être engagé.',
        },
      },
      done: {
        succeeded: 'Paiement marqué reçu : la facture est payée.',
        failed: 'Paiement marqué échoué.',
      },
      forbidden: 'Refusé : solder une transaction est réservé à l’administrateur de la plateforme.',
      failed: 'La transaction n’a pas été soldée : {{detail}}',
    },
  },
  paymentMethods: {
    title: 'Moyens de paiement',
    subtitle:
      'Le moyen favori règle les factures. Prélèvement SEPA sous mandat ou virement bancaire, sans service externe.',
    searchPlaceholder: 'Filtrer par titulaire, IBAN ou mandat…',
    new: 'Ajouter un moyen de paiement',
    newTitle: 'Ajouter un moyen de paiement',
    newSubtitle: 'Le premier moyen ajouté devient le favori de l’organisation.',
    chooseKind: 'Type de moyen de paiement',
    columns: {
      kind: 'Type',
      detail: 'Compte',
      mandate: 'Mandat',
      status: 'Statut',
    },
    kinds: {
      sepa: 'Prélèvement SEPA',
      in_person: 'Virement bancaire',
      credit_card: 'Carte',
      trial: 'Essai',
      paypal: 'PayPal',
    },
    statuses: {
      succeeded: 'Valide',
      pending: 'En attente',
      failed: 'Invalide',
    },
    favorite: 'Favori',
    setFavorite: 'Définir comme favori',
    favoriteSet: '{{name}} est désormais le moyen favori.',
    favoriteFailed: 'Le favori n’a pas changé : {{detail}}',
    transferDetail: 'Référence : le numéro de chaque facture',
    signedAt: 'Signé le {{date}}',
    created: 'Moyen de paiement ajouté.',
    deleted: '{{name}} supprimé.',
    deleteTitle: 'Supprimer le moyen de paiement',
    deleteLabel: 'Supprimer {{name}}',
    confirmDelete: 'Supprimer {{name}} ? Les paiements déjà engagés ne sont pas annulés.',
    confirmDeleteFavorite:
      '{{name}} est le moyen favori : le serveur refusera sa suppression. Désignez d’abord un autre favori.',
    errors: {
      CAN_NOT_DELETE_FAVORITE: 'Le moyen favori ne se supprime pas : désignez d’abord un autre favori.',
      CAN_NOT_SET_FAVORITE: 'Seul un moyen de paiement valide peut devenir le favori.',
    },
    empty: {
      title: 'Aucun moyen de paiement',
      description: 'Ajoutez un mandat de prélèvement SEPA ou le virement bancaire pour régler les factures.',
    },
    choices: {
      sepa: {
        title: 'Prélèvement SEPA',
        description: 'Vous signez un mandat ; l’opérateur prélève chaque facture sur votre compte.',
      },
      transfer: {
        title: 'Virement bancaire',
        description:
          'Vous virez chaque facture aux coordonnées de l’opérateur, avec son numéro en référence.',
      },
    },
    sepa: {
      mandateTitle: 'Mandat de prélèvement SEPA',
      creditor: 'Créancier',
      identifier: 'Identifiant créancier SEPA (ICS)',
      reference: 'Référence unique du mandat (RUM)',
      paymentType: 'Type de paiement',
      recurrent: 'Récurrent',
      theCreditor: 'le créancier',
      mandateText:
        'En signant ce formulaire de mandat, vous autorisez {{creditor}} à envoyer des instructions à votre banque pour débiter votre compte, et votre banque à débiter votre compte conformément aux instructions de {{creditor}}.',
      rights:
        'Vous bénéficiez du droit d’être remboursé par votre banque selon les conditions décrites dans la convention que vous avez passée avec elle. Une demande de remboursement doit être présentée dans les 8 semaines suivant la date de débit de votre compte pour un prélèvement autorisé.',
      fields: {
        holderName: 'Titulaire du compte',
        holderNameHint: 'Le nom du débiteur, tel qu’il figure sur le relevé d’identité bancaire.',
        iban: 'IBAN',
        accept: 'J’accepte le mandat de prélèvement SEPA ci-dessus',
        acceptHint: 'La date d’acceptation est enregistrée comme date de signature du mandat.',
      },
      errors: {
        iban: 'IBAN invalide : vérifiez le code pays, la clé et le nombre de caractères.',
        accept: 'Le mandat doit être accepté pour être enregistré.',
      },
      submit: 'Signer le mandat',
    },
    transfer: {
      title: 'Coordonnées de virement de l’opérateur',
      beneficiary: 'Bénéficiaire',
      iban: 'IBAN',
      bic: 'BIC',
      reference:
        'Référence à porter sur chaque virement : le numéro de la facture (par exemple GS-2026-0042). Le paiement est constaté à réception.',
      submit: 'Ajouter le virement bancaire',
    },
    stripe: {
      title: 'Paiement par carte via Stripe : non configuré',
      description:
        'Cette installation encaisse par Stripe, mais aucune clé publiable n’est configurée : la carte ne peut pas être saisie ici. Demandez à l’opérateur de la configurer.',
      cardTitle: 'Carte bancaire',
      cardHint:
        'La carte est saisie dans un champ sécurisé de Stripe : son numéro ne passe jamais par graal, qui n’en garde que les quatre derniers chiffres.',
      submit: 'Enregistrer la carte',
      notReady: 'Le champ de carte n’est pas prêt.',
      refused: 'La carte a été refusée.',
      failed: 'Carte non enregistrée : {{detail}}',
      loadFailed: 'Stripe.js n’a pas pu être chargé : le paiement par carte est indisponible pour le moment.',
    },
  },
  /** ADR 0089 : la page « Tarifs » — la grille à la minute (SaaS) ou de refacturation interne (self-hosted). */
  pricing: {
    title: {
      saas: 'Tarifs à la minute',
      selfHosted: 'Grille de refacturation interne',
    },
    subtitle: {
      saas: 'La grille de graal.systems, en euros hors taxes. Ce que vous voyez ici est ce que la facture mensuelle applique.',
      selfHosted:
        'Les prix internes posés par l’administrateur de votre plateforme pour refacturer les consommations aux projets et aux directions. L’abonnement à l’éditeur, lui, se paie au vCPU, hors de cette grille.',
    },
    rule: 'Facturé à la minute entamée, du démarrage effectif à la fin, multiplié par le nombre d’instances. Prix d’une minute = prix mensuel ÷ 43 200.',
    calculator: {
      title: 'Estimer une charge',
      description:
        'Choisissez un type d’instance, un nombre d’instances et une durée. Le total est calculé par graal-api, avec la même règle que les coûts et la facture.',
      type: 'Type d’instance',
      typePlaceholder: 'Choisir un type',
      instances: 'Instances',
      minutes: 'Durée (minutes)',
      shortcuts: 'Raccourcis de durée',
      oneHour: '1 h',
      eightHours: '8 h',
      oneDay: '1 j',
      thirtyDays: '30 j',
      duration: '{{minutes}} min facturées ({{hours}} h)',
      perMinute: 'Par minute',
      cost: 'Coût HT',
      add: 'Ajouter une charge',
      remove: 'Retirer cette charge',
      charge: 'Charge {{index}}',
      incomplete: 'Ligne incomplète : choisissez un type, au moins une instance et une minute.',
      totalPerMinute: 'Coût par minute, toutes charges',
      total: 'Total HT',
      exact: 'Montant exact : {{amount}}. La facture arrondit chaque type d’instance au centime sur le mois.',
      source: 'Calculé par graal-api (POST /estimations), comme les coûts et la facture.',
      computing: 'Calcul…',
      nothing: 'Ajoutez une charge complète pour estimer son coût.',
      mismatch:
        'L’estimation du serveur ne correspond pas à la grille affichée : la grille a peut-être changé. Rechargez la page.',
    },
    grid: {
      title: {
        saas: 'Grille des types d’instance',
        selfHosted: 'Grille interne des types d’instance',
      },
      description:
        'Prix à la minute arrondis à 4 décimales pour l’affichage ; le calcul garde le prix exact.',
      searchPlaceholder: 'Filtrer par type ou famille…',
      columns: {
        type: 'Type d’instance',
        family: 'Famille',
        vcpu: 'vCPU',
        memory: 'Mémoire',
        gpu: 'GPU',
        perMinute: '€ HT / minute',
        perHour: '€ HT / heure',
        monthly: 'Prix mensuel stocké',
      },
      unsized: 'Absent du catalogue des types d’instance : taille inconnue.',
      empty: {
        saas: {
          title: 'Aucun prix publié',
          description: 'La grille de graal.systems n’est pas encore posée sur cette installation.',
        },
        selfHosted: {
          title: 'Aucun prix interne',
          description:
            'L’administrateur de la plateforme pose la grille de refacturation (prix mensuel par type d’instance) ; rien n’est refacturé avant.',
        },
      },
    },
    families: {
      development: 'Développement',
      general: 'Usage général',
      'compute-optimized': 'Calcul',
      compute: 'Calcul',
      'memory-optimized': 'Mémoire',
      memory: 'Mémoire',
      gpu: 'GPU',
    },
    others: {
      title: 'Autres produits de la grille',
      EGRESS: 'Trafic sortant, par Go',
      LOGS: 'Journaux, par Go',
      SUPPORT: 'Support, à l’unité',
    },
    gpus_one: '{{count}} GPU',
    gpus_other: '{{count}} GPU',
    none: '—',
  },
};
